import React from 'react';
import { Col, Row } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import { WorkToggle } from './WorkToggle';
import { TimeSelect } from './TimeSelect';
import { ExceptionInclude, ExceptionExclude } from '../types/workingHours';
import DatePicker from '@/components/DatePicker';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '@/components/ui/popover';
import dayjs from 'dayjs';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';

const MemoPopoverContent = ({
    memo,
    onChange,
    onClose,
}: {
    memo: string;
    onChange: (val: string) => void;
    onClose: () => void;
}) => {
    return (
        <PopoverContent
            align="start"
            sideOffset={7}
            className="py-[18px] px-[22px] bg-white rounded-[12px] border border-solid border-[#E6E8EA] z-[100] shadow-lg"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
            onInteractOutside={(e: Event) => {
                e.preventDefault();
                onClose();
            }}
        >
            <RegularText fontSize={16} color="#6B7280">
                메모를 입력하세요
            </RegularText>
            <div className="flex bg-white items-center justify-between mt-[15px] border border-solid border-[#E6E8EA] rounded-[9px] focus-within:border-primary transition-colors">
                <input
                    type="text"
                    value={memo || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onClose();
                        }
                    }}
                    className="flex px-[17px] h-[40px] w-full text-[16px] text-[#2D3642] font-noto-regular outline-none bg-transparent rounded-[9px]"
                    autoFocus
                />
                {memo && (
                    <div className="flex ml-[4px] mr-[16px] cursor-pointer" onClick={() => onChange('')}>
                        <SvgIcon name="SvgCircleX" size={18} fill={'#A7B0B9'} />
                    </div>
                )}
            </div>
        </PopoverContent>
    );
};

export const ExceptionScheduleTab = ({
    exceptionIncludes,
    exceptionExcludes,
    includeEnabled,
    excludeEnabled,
    onIncludeToggle,
    onExcludeToggle,
    onAddInclude,
    onRemoveInclude,
    onUpdateInclude,
    onAddExclude,
    onRemoveExclude,
    onUpdateExclude,
}: {
    exceptionIncludes: ExceptionInclude[];
    exceptionExcludes: ExceptionExclude[];
    includeEnabled: boolean;
    excludeEnabled: boolean;
    onIncludeToggle: () => void;
    onExcludeToggle: () => void;
    onAddInclude: () => void;
    onRemoveInclude: (id: string) => void;
    onUpdateInclude: (
        id: string,
        field: 'date' | 'startTime' | 'endTime' | 'memo' | 'isSaved' | 'isMemoEditing',
        value: any,
    ) => void;
    onAddExclude: () => void;
    onRemoveExclude: (id: string) => void;
    onUpdateExclude: (id: string, field: 'date' | 'memo' | 'isSaved' | 'isMemoEditing', value: any) => void;
}) => {
    return (
        <Col>
            {/* 근무 시간 포함 섹션 */}
            <Row className="items-center justify-between mb-[26px]">
                <Row className="items-center">
                    <MediumText fontSize={18} lineHeight={24} color="#454F5D" className="mr-[20px]">
                        근무 시간 포함
                    </MediumText>
                    <div className="w-[44px] h-[26px] bg-[#E6F2EC] rounded-[13px] flex items-center justify-center">
                        <RegularText fontSize={13} letterSpacing={-0.5} color="#12A25D">
                            IN
                        </RegularText>
                    </div>
                </Row>
                <WorkToggle value={includeEnabled} onToggle={onIncludeToggle} />
            </Row>

            {includeEnabled && (
                <Col className="mb-[50px]">
                    <Col className="flex flex-col gap-[17px]">
                        {exceptionIncludes.map((item) => {
                            if (item.isSaved) {
                                return (
                                    <Col key={item.id} className="">
                                        <Popover
                                            open={item.isMemoEditing}
                                            onOpenChange={(open: boolean) => onUpdateInclude(item.id, 'isMemoEditing', open)}
                                        >
                                            <PopoverAnchor asChild>
                                                <div className="flex h-[54px] bg-white rounded-[12px] border border-solid border-[#E6E8EA] items-center justify-between px-[22px]">
                                                    <Row className="flex flex-row gap-[14px]">
                                                        <RegularText fontSize={16} color="#3D4244" letterSpacing={-0.5}>
                                                            {`${dayjs(item.date).locale('ko').format('YYYY.MM.DD (ddd)')}`}
                                                        </RegularText>
                                                        <RegularText fontSize={16} color="#3D4244" letterSpacing={-0.5}>
                                                            {`${dayjs(item.startTime).locale('ko').format('A h시 mm분')} ~ ${dayjs(item.endTime).locale('ko').format('A h시 mm분')}`}
                                                        </RegularText>
                                                    </Row>
                                                    <Row className="gap-[28px] items-center">
                                                        <PopoverTrigger asChild>
                                                            <div className="cursor-pointer">
                                                                <SvgIcon
                                                                    name="SvgEdit"
                                                                    size={20}
                                                                    fill="none"
                                                                    stroke={item.memo ? '#3D73DD' : '#9CA3AF'}
                                                                />
                                                            </div>
                                                        </PopoverTrigger>
                                                        {exceptionIncludes.length > 1 && (
                                                            <SvgIcon
                                                                name="SvgCircleX"
                                                                size={24}
                                                                fill={'#A7B0B9'}
                                                                className="cursor-pointer"
                                                                onClick={() => onRemoveInclude(item.id)}
                                                            />
                                                        )}
                                                    </Row>
                                                </div>
                                            </PopoverAnchor>
                                            <MemoPopoverContent
                                                memo={item.memo || ''}
                                                onChange={(val) => onUpdateInclude(item.id, 'memo', val)}
                                                onClose={() => onUpdateInclude(item.id, 'isMemoEditing', false)}
                                            />
                                        </Popover>
                                    </Col>
                                );
                            }

                            return (
                                <Row key={item.id} className="items-center ">
                                    <div className="flex items-center ">
                                        <DatePicker
                                            value={item.date ? dayjs(item.date) : dayjs()}
                                            onChange={(val) =>
                                                onUpdateInclude(item.id, 'date', val.format('YYYY-MM-DD'))
                                            }
                                            dateFormat="YYYY.MM.DD(ddd)"
                                            renderTrigger={({ formattedDate, toggle, isHovered, isOpen }) => (
                                                <Button
                                                    variant="outline"
                                                    className={`border border-solid h-[54px] rounded-[12px] mr-[12px] w-[180px] justify-between transition-colors ${
                                                        isOpen || isHovered ? 'border-primary' : 'border-[#E6E8EA]'
                                                    }`}
                                                    onClick={toggle}
                                                >
                                                    <RegularText fontSize={16} color="#39414C" letterSpacing={-0.5}>
                                                        {formattedDate}
                                                    </RegularText>
                                                    <SvgIcon
                                                        name="SvgDownStrokeArrow"
                                                        size={12}
                                                        fill={colors.trans}
                                                        stroke={isOpen || isHovered ? colors.primary : '#A7B0B9'}
                                                        strokeWidth={1.25}
                                                    />
                                                </Button>
                                            )}
                                        />
                                        <TimeSelect
                                            value={item.startTime}
                                            onChange={(val) => onUpdateInclude(item.id, 'startTime', val)}
                                            disabled={false}
                                            placeholder="시작 시간"
                                            compact
                                        />
                                        <RegularText fontSize={16} lineHeight={20} color="#6B7280" className="mx-[8px]">
                                            ~
                                        </RegularText>
                                        <TimeSelect
                                            value={item.endTime}
                                            onChange={(val) => onUpdateInclude(item.id, 'endTime', val)}
                                            disabled={false}
                                            placeholder="종료 시간"
                                            compact
                                            allowDayEnd
                                        />
                                    </div>
                                    <div className="relative flex items-center justify-center ml-[12px]">
                                        <div
                                            className={`w-[60px] h-[40px] rounded-[20px] flex items-center justify-center transition-colors ${
                                                item.date && item.startTime && item.endTime
                                                    ? 'bg-[#3D4244] cursor-pointer'
                                                    : 'bg-[#E6E8EA] cursor-not-allowed'
                                            }`}
                                            onClick={() => {
                                                if (!item.date || !item.startTime || !item.endTime) {
                                                    return; // Do nothing if inactive
                                                }
                                                onUpdateInclude(item.id, 'isSaved', true);
                                            }}
                                        >
                                            <RegularText
                                                fontSize={16}
                                                color={
                                                    item.date && item.startTime && item.endTime ? '#FFFFFF' : '#9CA3AF'
                                                }
                                            >
                                                완료
                                            </RegularText>
                                        </div>
                                        {item.date && item.startTime && item.endTime && (
                                            <div className="absolute bottom-[calc(100%+12px)] left-1/2 -translate-x-1/2  shadow-sm w-max z-10 flex items-center justify-center bg-trans">
                                                <div className=" px-[16px] py-[10px] bg-[#344055] rounded-[12px]">
                                                    <RegularText fontSize={14} color="#FFFFFF">
                                                        완료를 눌러야 포함돼요!
                                                    </RegularText>
                                                </div>
                                                <div className=" absolute top-[100%] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-trans border-r-trans border-t-[#344055] border-solid bg-trans " />
                                            </div>
                                        )}
                                    </div>
                                    {exceptionIncludes.length > 1 && (
                                        <div
                                            className="flex  items-center justify-center ml-[12px] cursor-pointer"
                                            onClick={() => onRemoveInclude(item.id)}
                                        >
                                            <SvgIcon name="SvgCircleX" size={24} fill={'#A7B0B9'} />
                                        </div>
                                    )}
                                </Row>
                            );
                        })}
                    </Col>
                    <Row className="items-center cursor-pointer mt-[24px] gap-[8px]" onClick={onAddInclude}>
                        <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                        <RegularText fontSize={18} color="#3D73DD">
                            추가하기
                        </RegularText>
                    </Row>
                </Col>
            )}

            {/* 근무 시간 제외 섹션 */}
            <Row className="items-center justify-between mb-[16px] mt-[8px]">
                <Row className="items-center">
                    <MediumText fontSize={18} lineHeight={24} color="#454F5D" className="mr-[12px]">
                        근무 시간 제외
                    </MediumText>
                    <div className="w-[44px] h-[26px] bg-[#FEEAEB] rounded-[13px] flex items-center justify-center">
                        <RegularText fontSize={13} letterSpacing={-0.5} color="#D93043">
                            OUT
                        </RegularText>
                    </div>
                </Row>
                <WorkToggle value={excludeEnabled} onToggle={onExcludeToggle} />
            </Row>

            {excludeEnabled && (
                <Col>
                    {exceptionExcludes.map((item) => {
                        if (item.isSaved) {
                            return (
                                <Col key={item.id} className="mb-[10px]">
                                    <Popover
                                        open={item.isMemoEditing}
                                        onOpenChange={(open: boolean) => onUpdateExclude(item.id, 'isMemoEditing', open)}
                                    >
                                        <PopoverAnchor asChild>
                                            <div className="flex h-[54px] bg-white rounded-[12px] border border-solid border-[#E6E8EA] items-center justify-between px-[22px]">
                                                <RegularText fontSize={16} color="#3D4244">
                                                    {dayjs(item.date).locale('ko').format('YYYY. MM. DD (ddd)')}
                                                </RegularText>
                                                <Row className="gap-[12px] items-center">
                                                    <PopoverTrigger asChild>
                                                        <div className="cursor-pointer">
                                                            <SvgIcon
                                                                name="SvgEdit"
                                                                size={20}
                                                                fill="none"
                                                                stroke={item.memo ? '#3D73DD' : '#9CA3AF'}
                                                            />
                                                        </div>
                                                    </PopoverTrigger>
                                                    {exceptionExcludes.length > 1 && (
                                                        <div
                                                            className="rounded-full flex items-center justify-center cursor-pointer"
                                                            onClick={() => onRemoveExclude(item.id)}
                                                        >
                                                            <SvgIcon name="SvgCircleX" size={24} fill={'#A7B0B9'} />
                                                        </div>
                                                    )}
                                                </Row>
                                            </div>
                                        </PopoverAnchor>
                                        <MemoPopoverContent
                                            memo={item.memo || ''}
                                            onChange={(val) => onUpdateExclude(item.id, 'memo', val)}
                                            onClose={() => onUpdateExclude(item.id, 'isMemoEditing', false)}
                                        />
                                    </Popover>
                                </Col>
                            );
                        }

                        return (
                            <Row key={item.id} className="items-center mb-[10px]">
                                <DatePicker
                                    value={item.date ? dayjs(item.date) : dayjs()}
                                    onChange={(val) => onUpdateExclude(item.id, 'date', val.format('YYYY-MM-DD'))}
                                    dateFormat="YYYY.MM.DD(ddd)"
                                    renderTrigger={({ formattedDate, toggle, isHovered, isOpen }) => (
                                        <Button
                                            variant="outline"
                                            className={`border border-solid h-[54px] rounded-[12px] mr-[12px] w-[202px] justify-between transition-colors ${
                                                isOpen || isHovered ? 'border-primary' : 'border-[#E6E8EA]'
                                            }`}
                                            onClick={toggle}
                                        >
                                            <RegularText fontSize={16} color="#39414C" letterSpacing={-0.5}>
                                                {formattedDate}
                                            </RegularText>
                                            <SvgIcon
                                                name="SvgDownStrokeArrow"
                                                size={12}
                                                fill={colors.trans}
                                                stroke={isOpen || isHovered ? colors.primary : '#9CA3AF'}
                                            />
                                        </Button>
                                    )}
                                />
                                <div className="relative flex items-center justify-center ml-[12px]">
                                    <div
                                        className={`w-[60px] h-[40px] rounded-[20px] flex items-center justify-center transition-colors ${
                                            item.date
                                                ? 'bg-[#3D4244] cursor-pointer'
                                                : 'bg-[#E6E8EA] cursor-not-allowed'
                                        }`}
                                        onClick={() => {
                                            if (!item.date) {
                                                return; // Do nothing if inactive
                                            }
                                            onUpdateExclude(item.id, 'isSaved', true);
                                        }}
                                    >
                                        <RegularText fontSize={16} color={item.date ? '#FFFFFF' : '#6B7280'}>
                                            완료
                                        </RegularText>
                                    </div>
                                    {item.date && (
                                        <div className="absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 px-[16px] py-[10px] bg-[#3D4244] rounded-[12px] shadow-sm w-max z-10 flex items-center justify-center">
                                            <RegularText fontSize={14} color="#FFFFFF">
                                                완료를 눌러야 제외돼요!
                                            </RegularText>
                                            <div className="absolute top-[100%] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent border-t-[#3D4244]" />
                                        </div>
                                    )}
                                </div>
                                {exceptionExcludes.length > 1 && (
                                    <div
                                        className="rounded-full flex items-center justify-center ml-[12px] cursor-pointer"
                                        onClick={() => onRemoveExclude(item.id)}
                                    >
                                        <SvgIcon name="SvgCircleX" size={24} fill={'#A7B0B9'} />
                                    </div>
                                )}
                            </Row>
                        );
                    })}
                    <Row className="items-center cursor-pointer mt-[24px] gap-[8px]" onClick={onAddExclude}>
                        <SvgIcon name="SvgPlus" size={16} fill={colors.primary} />
                        <RegularText fontSize={18} color="#3D73DD">
                            추가하기
                        </RegularText>
                    </Row>
                </Col>
            )}
        </Col>
    );
};
